import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import CenteredLoading from "../../../../../components/CenteredLoading";
import { useBillingPlans } from "../../../../../context/billingPlans";
import { useToast } from "../../../../../context/toast";
import { CardPaymentMethod } from "../../../../../utils/types";
import PaymentMethod from "../../../../Plans/PlansCheckout/PaymentMethod";

const BillingPaymentMethod: React.FC = () => {
  const { addPaymentMethod, newPlan } = useBillingPlans();
  const { setModalWarningConfig } = useToast();
  const {
    defaultPaymentMethod,
    loading,
    deletePaymentMethod,
    changeDefaultPaymentMethod
  } = useBillingPlans();
  const history = useHistory();

  const isDefaultPayment = useMemo(
    () => newPlan?.paymentMethod?.id === defaultPaymentMethod?.id,
    [defaultPaymentMethod?.id, newPlan?.paymentMethod]
  );

  const onScrollTop = useCallback(() => {
    const workspaceSettingsContainer = document.getElementById(
      "workspace-settings-container"
    );

    workspaceSettingsContainer?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const renderPaymentActions = useMemo(
    () => (
      <div className="flex items-center justify-between w-full">
        <span className="text-lg font-bold dark:text-white mr-4">
          Edit Payment Method
        </span>
        {!isDefaultPayment && (
          <div className="flex items-center">
            <button
              onClick={() => {
                setModalWarningConfig({
                  open: true,
                  title: "Default payment method",
                  confirmButtonText: "Save",
                  cancelButtonText: "Close",
                  confirmButtonClassname:
                    "w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-hotspotty-400 text-base font-medium text-white hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 md:ml-3 sm:w-auto sm:text-sm",
                  message:
                    "Do you want to set this payment method as default? Your upcoming payments will be deducted from this payment method",
                  resolve: async () => {
                    await changeDefaultPaymentMethod(
                      newPlan?.paymentMethod?.id
                    );
                  }
                });
              }}
              disabled={
                !newPlan?.paymentMethod || loading.changeDefaultPaymentMethod
              }
              className="text-xs p-2 mr-3 rounded-md border disabled:opacity-75 disabled:cursor-not-allowed dark:border-hotspotty-400 dark:bg-hotspotty-400 dark:hover:bg-hotspotty-500 dark:hover:border-hotspotty-500 focus:outline-none"
            >
              {loading.changeDefaultPaymentMethod ? (
                <span className="flex items-center">
                  <CenteredLoading className="w-5 h-5 mr-2" /> Processing...
                </span>
              ) : (
                "Set as Default"
              )}
            </button>

            <button
              onClick={() => {
                setModalWarningConfig({
                  open: true,
                  title: "Delete payment method",
                  confirmButtonText: "Delete Payment Method",
                  cancelButtonText: "Close",
                  message:
                    "Are you sure you want to delete this payment method?",
                  resolve: async () => {
                    await deletePaymentMethod(newPlan?.paymentMethod?.id);

                    history.push("/workspace/settings/billing");
                  }
                });
              }}
              disabled={!newPlan?.paymentMethod || loading.deletePaymentMethod}
              className="text-xs p-2 rounded-md border disabled:opacity-75 disabled:cursor-not-allowed dark:border-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:hover:border-gray-600 focus:outline-none"
            >
              {loading.deletePaymentMethod ? (
                <span className="flex items-center">
                  <CenteredLoading className="w-5 h-4 mr-2" /> Deleting...
                </span>
              ) : (
                "Delete"
              )}
            </button>
          </div>
        )}
      </div>
    ),
    [
      changeDefaultPaymentMethod,
      deletePaymentMethod,
      history,
      isDefaultPayment,
      loading.changeDefaultPaymentMethod,
      loading.deletePaymentMethod,
      newPlan?.paymentMethod,
      setModalWarningConfig
    ]
  );

  return (
    <>
      <div className="flex items-center pb-2 border-b border-gray-200 dark:border-gray-500">
        <h2
          className="text-2xl font-semibold hover:text-hotspotty-300 cursor-pointer mr-1"
          onClick={() => history.push("/workspace/settings/billing")}
        >
          Billing details
        </h2>
        <p className="text-2xl font-semibold">/ Payment method</p>
      </div>

      <PaymentMethod
        title={
          !newPlan.paymentMethod ? (
            "Add payment method"
          ) : (
            <div className="flex items-center w-full">
              {renderPaymentActions}
            </div>
          )
        }
        onComplete={async (paymentMethod?: CardPaymentMethod) => {
          if (paymentMethod?.id) {
            await addPaymentMethod(paymentMethod?.id);
          }

          onScrollTop();
          history.push("/workspace/settings/billing");
        }}
        current={true}
        onBack={() => {
          onScrollTop();
          history.push("/workspace/settings/billing");
        }}
        withCollapse={false}
      />
    </>
  );
};

export default BillingPaymentMethod;
